import { useEffect, useContext } from 'react'

import { StudyTelemetryContext } from './StudyTelemetryContext'
import { resolveNode } from './resolveNode'

// The highlight/copy listener that the workspace repeats verbatim in
// TaskDescription, TestCases, CodeEditor and Terminal — factored into one hook.
// Same listeners (mouseup / keyup / copy), same de-dupe on repeated selections,
// same { selected_text, timestamp } payload; only the event names differ per host.
//
// `target` is a ref OR a raw element. Panels that mount and unmount with the
// component can pass a ref; panels that come and go while it stays mounted (the
// DESC content, which the COSMO tab replaces) must pass the element via a
// callback ref, so this effect re-runs and re-binds when the node is swapped.
export const useSelectionTelemetry = (target, selectEvent, copyEvent) => {
  const { logTelemetryEvent } = useContext(StudyTelemetryContext)

  useEffect(() => {
    const el = resolveNode(target)
    if (!el) return

    let lastSelection = ''

    const handleSelectionChange = () => {
      const selectedText = window.getSelection().toString()
      if (selectedText && selectedText !== lastSelection) {
        lastSelection = selectedText
        logTelemetryEvent(selectEvent, {
          selected_text: selectedText,
          timestamp: Date.now()
        })
      } else if (!selectedText) {
        lastSelection = ''
      }
    }

    const handleCopy = () => {
      const selectedText = window.getSelection().toString()
      if (selectedText) {
        logTelemetryEvent(copyEvent, {
          selected_text: selectedText,
          timestamp: Date.now()
        })
      }
    }

    el.addEventListener('mouseup', handleSelectionChange)
    el.addEventListener('keyup', handleSelectionChange)
    el.addEventListener('copy', handleCopy)

    return () => {
      el.removeEventListener('mouseup', handleSelectionChange)
      el.removeEventListener('keyup', handleSelectionChange)
      el.removeEventListener('copy', handleCopy)
    }
  }, [target, selectEvent, copyEvent, logTelemetryEvent])
}
